// The five left-rail workspaces (seven until 2026-10-04).
//
//   Site          now, the site, the sources — the verdict and its arming
//                 controls moved to the strip above the map on 2026-10-09
//   Skill         why believe any of it — both bases, never one
//   Observations  map layers, and a clicked source against the intake
//   Systems       upstream health, and the verification ladder at runtime
//   Feedback      unchanged from fleet
//
// Intake and Drivers are gone from the rail. Both were reading, not controls or
// generated numbers, and the rule for this column is that every tab is one of
// those two. Their render functions remain below, unreachable, until someone
// decides whether that material belongs in Docs.
//
// The one rule running through all of them: A NUMBER WITHOUT ITS BASIS IS NOT A
// NUMBER HERE. Every accuracy figure in this product differs by roughly a fifth
// depending on whether PG&E's own thermometer is an input, so a panel that
// prints "0.70 °F at one day" without saying which basis that is has misled the
// reader even though the digits are right.

import { type Payload, type SourcePoint, num, f1 } from "./payload.js";
import { feedHealth, statusWord, type Feed } from "./feeds.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls !== undefined) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function section(into: HTMLElement, title: string): HTMLElement {
  const s = el("section", "wssec");
  s.appendChild(el("h5", "wssec-t", title));
  const b = el("div", "wssec-b");
  s.appendChild(b);
  into.appendChild(s);
  return b;
}

function para(into: HTMLElement, text: string): void {
  into.appendChild(el("p", "note", text));
}

/** A definition row: label on the left, value on the right. */
function row(into: HTMLElement, label: string, value: string, note?: string): void {
  const r = el("div", "fp-row");
  r.appendChild(el("span", "fp-k", label));
  r.appendChild(el("span", "fp-v", value));
  if (note !== undefined) r.appendChild(el("span", "fp-n", note));
  into.appendChild(r);
}

function table(into: HTMLElement, head: string[], rows: string[][]): void {
  const wrap = el("div", "tw");
  const t = el("table");
  const thead = el("thead");
  const htr = el("tr");
  for (const h of head) htr.appendChild(el("th", undefined, h));
  thead.appendChild(htr);
  const tb = el("tbody");
  for (const r of rows) {
    const tr = el("tr");
    for (const c of r) tr.appendChild(el("td", undefined, c));
    tb.appendChild(tr);
  }
  t.append(thead, tb);
  wrap.appendChild(t);
  into.appendChild(wrap);
}

export interface PanelDeps {
  payload: Payload;
  /** Live arming state, read at render time. */
  threshold: () => number;
  lead: () => number;
  /** The source the operator last clicked on the map, if any. */
  focusSource?: () => SourcePoint | undefined;
  /** Toggle a map layer. Additive and persistent — never a rail concern. */
  onLayer?: (id: string, on: boolean) => void;
}

export function renderPanel(id: string, into: HTMLElement, d: PanelDeps): void {
  const p = d.payload;
  switch (id) {
    case "site":
      return site(into, d);
    case "intake":
      return intake(into, p);
    case "drivers":
      return drivers(into, p);
    case "skill":
      return skill(into, p);
    case "obs":
      return observations(into, p, d);
    case "systems":
      return systems(into, p);
    case "feedback":
      return feedback(into);
    default:
      para(into, "This workspace has nothing in it yet.");
  }
}

// --- Site --------------------------------------------------------------------
function site(into: HTMLElement, d: PanelDeps): void {
  const p = d.payload;

  // The verdict and the controls that arm it are NOT here. From 2026-10-04 to
  // 2026-10-09 they led this workspace, where the fleet console arms vessel ×
  // operation; Les asked for the prediction at the top of the page, and
  // console.ts now builds it as a strip above the map. Site keeps the reading
  // that does not depend on a station: now, the site, the sources.
  const n = section(into, "Now");
  row(n, "Estimate", f1(p.current?.estimate_f),
    p.current?.band_f !== undefined ? `± ${f1(p.current.band_f, "")} band` : undefined);
  row(n, "Margin", f1(p.current?.margin_f), `to ${f1(p.thresholds?.action_f)}`);
  row(n, "Seasonal normal", f1(p.current?.normal_f), "for this day of year");

  // Removed 2026-10-04: "Working conditions" (wind, sea, daylight) and the
  // "Reference" pointer. Cody: "take out things that don't matter, like the wind
  // ... focus on predicting the range." The conditions briefing was a real
  // feature with a real audience (their divers and small boats), but it was a
  // second product sharing a column with the first, and the first is the one
  // being sold. It lives on in conditions.ts, unmounted, for when it is wanted.
}

// --- Intake ------------------------------------------------------------------
function intake(into: HTMLElement, p: Payload): void {
  const b = section(into, "The structure");
  row(b, "Draw", "surface to −9.6 m");
  row(b, "Circulating water", "~2,486 MGD, four pumps");
  row(b, "Discharge", "outfall 001");

  const m = section(into, "The plant's own meter");
  row(m, "Public record to", p.last_reported ?? "—");
  const h = p.intake_hourly;
  if (h !== null && h !== undefined) {
    row(m, "Hourly export to", (h.last ?? "—").replace("T", " "),
      `${h.stale_days ?? "?"} d old`);
    row(m, "Points held", String(h.points?.length ?? 0), h.source);
  }
  para(m, "The public filing runs 45–110 days behind. That gap is what this " +
    "console fills, and it is the part nobody outside the plant can otherwise see.");

  const a = section(into, "What a live feed would buy");
  const g = p.skill_basis?.gain ?? [];
  if (g.length > 0) {
    table(a, ["lead", "external", "with meter", "gain"],
      g.map((r) => [`${r.lead ?? "?"} d`, f1(r.external_f), f1(r.with_meter_f),
        `${(r.gain_pct ?? 0) > 0 ? "+" : ""}${(r.gain_pct ?? 0).toFixed(0)} %`]));
    // Said plainly, because the shape of this table is easy to over-read: the
    // meter transforms the NOWCAST and barely touches the forecast.
    para(a, "The gain is concentrated at one day. Beyond that the meter is worth " +
      "a couple of per cent — the forecast is carried by the buoy and the " +
      "meteorology, not by the plant's own reading.");
  } else {
    para(a, "Not measured in this payload.");
  }
}

// --- Drivers -----------------------------------------------------------------
function drivers(into: HTMLElement, p: Payload): void {
  const b = section(into, "What the forecast is built from");
  table(b, ["input", "role", "weight"], [
    ["Buoy 46215, daily minimum", "the predictor", "91–95 % of recoverable error"],
    ["Port San Luis · NDBC 46218", "redundancy, same quantity elsewhere", "~6 % at one day"],
    ["Solar radiation", "skin-layer warming", "retained"],
    ["Wind", "mixing, which suppresses it", "retained"],
    ["Air temperature · cloud", "context", "~1 % between them"],
  ]);
  para(b, "Listed in the order they matter, not as peers. A panel showing five " +
    "equal rows would misrepresent the model: the buoy is nearly all of it.");

  const d = section(into, "Window");
  const arr = p.drivers ?? [];
  const past = arr.filter((x) => x.future !== true).length;
  const fut = arr.length - past;
  row(d, "Held", `${past} past + ${fut} forward days`);
  row(d, "Model", p.model?.kind ?? "—");
  row(d, "Trained", `${p.model?.trained_days ?? "—"} days`,
    `${p.model?.trained_from ?? "?"} → ${p.model?.trained_to ?? "?"}`);

  const r = section(into, "Redundancy");
  para(r, "NDBC buoys go offline for months at a time. If 46215 drops, the model " +
    "refits without it on the two remaining stations rather than carrying a " +
    "stale value forward — and this console says which set is live, in Systems.");
}

// --- Skill -------------------------------------------------------------------
//
// Every number here is READ from the payload, and the payload reads it from the
// JSON the research scripts write (build_panel.py: _load_skill_max,
// _load_coverage). Nothing is typed in. Cody, 2026-10-04: "make sure everything
// under Skill is correct or remove it — it's confusing." Removed: two blind-error
// tables on the daily MEAN (one for a with-meter variant that is not deployed),
// a baselines table whose first and third rows were identical, two detector
// tables from an older daily-mean run (13 crossings in 4,762 days) sitting under
// a model that now predicts the daily MAXIMUM, and prose quoting coverage
// figures from before the bound was repaired. What remains is one quantity —
// the daily max, the licence quantity — scored one way.
function pct(v: number | null | undefined, digits = 0): string {
  return typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(digits)} %` : "—";
}

function skill(into: HTMLElement, p: Payload): void {
  const sm = p.skill_max;
  const how = section(into, "How these are measured");
  para(how, `${sm?.protocol ?? "Blind walk-forward, refit monthly"} — each month predicted ` +
    "by a fit that saw only earlier data. The quantity is the intake daily MAXIMUM, the " +
    "licence quantity. Past days use the archived ocean analysis for every method, " +
    "because no source keeps sea-temperature forecasts as issued; these compare the " +
    "mappings onto the intake, not forecast skill.");
  if (sm === null || sm === undefined) {
    para(into, "Not measured in this payload — run research/max_benchmark.py and rebuild.");
    return;
  }
  const ours = sm.ours ?? [];

  const e = section(into, "Error by lead — sixteen years");
  // No bias column: it is −0.04 to −0.13 °F at every lead, which prints as
  // "-0.0 °F" and reads as a fault rather than as "unbiased".
  table(e, ["lead", "RMSE", "days"],
    ours.map((r) => [`${r.lead ?? "—"} d`, f1(r.rmse_f),
      r.n === undefined ? "—" : r.n.toLocaleString()]));

  const h = section(into, "Against Open-Meteo + offset — 2023 on, same days");
  table(h, ["lead", "Open-Meteo + offset", "SeaState", "lower error by"],
    (sm.head_to_head ?? []).map((r) => [`${r.lead ?? "—"} d`, f1(r.pge_rmse), f1(r.ours_rmse),
      pct(r.ours_wins_pct)]));
  const warm = (sm.warm_decile ?? []).filter((w) => typeof w.gain_pct === "number");
  const warmTxt = warm.length > 0
    ? ` On the warmest tenth of days the margin is ${warm.map((w) => `${pct(w.gain_pct)} at ${w.lead} d`).join(", ")} — clearly better at one day, level beyond three.`
    : "";
  para(h, "\"Open-Meteo + offset\" is our reconstruction of PG&E's incumbent from Les Bender's " +
    "description (3 Oct 2026: \"a simple offset model using the Open-Meteo forecasts\"), " +
    "given its best shot with a seasonal offset fitted on prior data. Their actual offset " +
    "and inputs are not known to us." + warmTxt +
    (sm.limitation ? ` ${sm.limitation}.` : ""));

  const c = section(into, "Catching a 64 °F day ahead of time");
  table(c, ["lead", "caught", "of", "false alarms", "CSI"],
    ours.map((r) => [`${r.lead ?? "—"} d`, String(r.hits ?? "—"), String(r.events ?? "—"),
      String(r.false_alarms ?? "—"),
      r.csi === undefined || r.csi === null ? "—" : r.csi.toFixed(2)]));
  const first = ours[0];
  const none = ours.filter((r) => (r.hits ?? 0) === 0 && (r.lead ?? 0) >= 3).map((r) => r.lead);
  para(c, (first !== undefined
    ? `At ${first.lead} d the forecast catches ${first.hits} of the ${first.events} days the intake reached 64 °F. `
    : "") +
    (none.length > 0
      ? `At ${none.join(" and ")} d it has caught none in sixteen years, and neither has the offset model. `
      : "") +
    "Plan on the short leads; treat the long ones as a heads-up, not a trigger.");

  const cov = p.decision?.coverage_by_lead ?? [];
  if (cov.length > 0) {
    const cv = section(into, "The decision bound");
    table(cv, ["lead", "days", "above the bound", "delivered"],
      cov.map((r) => [`${r.lead ?? "—"} d`, r.n === undefined ? "—" : r.n.toLocaleString(),
        String(r.breached ?? "—"),
        r.percentile === undefined ? "—" : `${r.percentile.toFixed(1)}th`]));
    const pcts = cov.map((r) => r.percentile).filter((v): v is number => typeof v === "number");
    const lo = Math.min(...pcts);
    const hi = Math.max(...pcts);
    para(cv, `Fitted as the ${((p.decision?.tau ?? 0.975) * 100).toFixed(1)}th percentile; ` +
      `measured blind it delivers the ${lo.toFixed(1)}${hi !== lo ? `–${hi.toFixed(1)}` : ""}th ` +
      "across leads. The tier is computed from this line.");
  }
}

// --- Observations ------------------------------------------------------------
//
// LAYERS, not prose. rail.ts is explicit that layer toggles live inside the
// Observations workspace and never in the rail itself — the rail holds
// exclusive workspaces, layers are additive and persistent, and mixing the two
// makes a click unpredictable. The fleet console mounts its layer control here
// for exactly that reason.
//
// The first version of this panel was three paragraphs about the daily-minimum
// basis. That prose belongs in the Drivers tab and in the paper; this tab is a
// control surface, and a control surface full of reading is a control surface
// nobody operates.
//
// createLayerControl() itself is NOT reused: it is bound to the fleet's own
// OVERLAYS list — EEZ boundaries, world ports, AIS, drifters — none of which
// exist for a client with one installation. What is reused is its SHAPE, so the
// gesture is the same one an operator already knows.
function observations(into: HTMLElement, p: Payload, deps: PanelDeps): void {
  const sources = p.sources ?? [];

  // The fleet console's layer control, markup for markup. `.layerctl` /
  // `.lc-body` / `.lc-swatch` and the label/input/small shape are all styled in
  // style.css already — writing a second set of classes for the same control
  // was how this ended up looking like a different product.
  const lay = document.createElement("details");
  lay.className = "layerctl layerctl-hosted";
  lay.open = true;
  const sum = document.createElement("summary");
  sum.textContent = "Layers";
  const body = el("div", "lc-body");
  lay.append(sum, body);
  for (const d of [
    { id: "field", label: "Sea-surface temperature",
      sub: "MUR 1 km · satellite skin · light = cooler, dark = warmer", sw: "#d66c2c" },
    { id: "sources", label: "Measuring network",
      sub: `${sources.length} points · click one to compare`, sw: "#f2b134" },
    { id: "labels", label: "Station names", sub: "on the markers", sw: "#16232e" },
  ]) {
    const lab = document.createElement("label");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = true;
    cb.dataset["layer"] = d.id;
    cb.addEventListener("change", () => deps.onLayer?.(d.id, cb.checked));
    const sw = el("span", "lc-swatch");
    sw.style.background = d.sw;
    const txt = document.createElement("span");
    txt.appendChild(document.createTextNode(d.label));
    txt.appendChild(el("small", undefined, d.sub));
    lab.append(cb, sw, txt);
    body.appendChild(lab);
  }
  into.appendChild(lay);

  // A clicked marker lands here, under the layers rather than above them: the
  // controls are what this tab is for, and the comparison is what a click adds.
  const focus = deps.focusSource?.();
  if (focus !== undefined && focus.kind !== "intake") {
    const b = section(into, focus.name ?? "Station");
    row(b, "Distance", `${(focus.km ?? 0).toFixed(2)} km from the intake`);
    row(b, "Basis", focus.basis ?? "daily minimum");
    row(b, "Role", focus.role ?? "—");
    const v = focus.vs_intake;
    if (v !== null && v !== undefined) {
      table(b, ["r", "bias", "sd", "days"], [[
        v.r === undefined ? "—" : v.r.toFixed(3),
        f1(v.bias_f), f1(v.sd_f), String(v.n ?? "—"),
      ]]);
    }
    sparkline(b, focus, sources.find((s) => s.kind === "intake"));
  }

  const n = section(into, "The network");
  if (sources.length > 0) {
    table(n, ["source", "km", "r", "sd"],
      sources.map((s) => [
        s.name ?? "—",
        s.kind === "intake" ? "—" : (s.km ?? 0).toFixed(1),
        s.vs_intake?.r === undefined ? "—" : s.vs_intake.r.toFixed(3),
        s.vs_intake?.sd_f === undefined ? "—" : f1(s.vs_intake.sd_f),
      ]));
    para(n, "A buoy 84 km offshore tracks the intake better than one 10 km away " +
      "inside a bay. Distance is not the ordering principle — which is what makes " +
      "this forecastable.");
  }
}

/** A small shared-axis trace: the station and the intake over the same window.
 *  Deliberately tiny and unlabelled beyond its caption — the numbers above are
 *  the finding; this is here to show the two lines move together. */
function sparkline(into: HTMLElement, s: SourcePoint, intake?: SourcePoint): void {
  const a = s.series ?? [];
  const b = intake?.series ?? [];
  if (a.length < 5) return;
  const box = section(into, "Both records, same window");
  const W = 260;
  const H = 76;
  const byDate = new Map(b.map((r) => [r[0], r[1]]));
  const vals = [...a.map((r) => r[1]), ...b.map((r) => r[1])].filter((v) => Number.isFinite(v));
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const span = hi - lo || 1;
  const x = (i: number): number => (i / Math.max(1, a.length - 1)) * (W - 2) + 1;
  const y = (v: number): number => H - 2 - ((v - lo) / span) * (H - 4);
  const path = (get: (d: string, i: number) => number | undefined): string => {
    const seg: string[] = [];
    let pen = false;
    a.forEach((r, i) => {
      const v = get(r[0], i);
      if (v === undefined || !Number.isFinite(v)) { pen = false; return; }
      seg.push(`${pen ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`);
      pen = true;
    });
    return seg.join(" ");
  };
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("class", "spark");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", `${s.name ?? "station"} and the intake over the same window`);
  const mk = (d: string, cls: string): void => {
    if (d === "") return;
    const el2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
    el2.setAttribute("d", d);
    el2.setAttribute("class", cls);
    svg.appendChild(el2);
  };
  mk(path((_d, i) => a[i]?.[1]), "spark-s");
  mk(path((d) => byDate.get(d)), "spark-i");
  box.appendChild(svg);
  const key = el("p", "note");
  key.textContent =
    `Heavy — the intake (daily maximum, the plant's own meter). ` +
    `Thin — ${s.name ?? "this station"} (${s.basis ?? "daily minimum"}). ` +
    `${lo.toFixed(1)}–${hi.toFixed(1)} °F.`;
  box.appendChild(key);
}

// --- Systems -----------------------------------------------------------------
// The runtime face of the verification ladder. Layers 1–4 are assertions run at
// build time; this is the same information where a client can see it, because a
// stale panel looks exactly like a fresh one.
function feedRow(into: HTMLElement, f: Feed): void {
  const r = el("div", `feed feed-${f.status}`);
  // Light AND word. Colour is never the only signal — this panel has to survive
  // a projector, a photocopier and a colour-blind reader, same rule the
  // conditions table follows by printing every number it colours.
  const dot = el("span", "feed-dot");
  dot.setAttribute("role", "img");
  dot.setAttribute("aria-label", statusWord(f.status));
  const head = el("div", "feed-h");
  head.append(dot, el("span", "feed-n", f.label), el("span", "feed-s", statusWord(f.status)));
  const age =
    f.ageDays === null
      ? "no date reported"
      : f.ageDays <= 0
        ? "today"
        : `${f.ageDays} d old${f.currentTo === null ? "" : ` · to ${f.currentTo}`}`;
  const meta = el("div", "feed-m");
  meta.append(el("span", undefined, age), el("span", "feed-c", f.cadence));
  r.append(head, meta);
  r.appendChild(el("div", "feed-r", f.role));
  // The reason only when it is not simply fine — a note on every row is noise
  // and teaches the reader to skip the ones that matter.
  if (f.status !== "ok" && f.note !== "") r.appendChild(el("div", "feed-note", f.note));
  into.appendChild(r);
}

function systems(into: HTMLElement, p: Payload): void {
  // Graded against the PAYLOAD's own today, not the browser clock: a panel built
  // yesterday must not look fresher because this machine's date rolled over.
  const feeds = feedHealth(p, p.today ?? new Date().toISOString().slice(0, 10));

  const b = section(into, "Feeds");
  para(b, "Each source against the cadence it is supposed to keep. The eSMR " +
    "filing is quarterly and is not late at 60 days; the plant's hourly export " +
    "is supposed to be continuous and is late at three. One rule for all of them " +
    "would be wrong in both directions.");
  for (const f of feeds) feedRow(b, f);

  // THE PLANT'S PUSH. The rows above are read from the payload; this one is
  // live, because it is the one feed PG&E themselves operate (server/ingest.go)
  // and its age should never be a build old. Absent until they send anything.
  const pf = section(into, "Plant feed");
  const line = el("p", "note", "Checking…");
  pf.appendChild(line);
  void fetch("/api/thermal/feed", { credentials: "same-origin", cache: "no-store" })
    .then(async (res) => (res.ok ? (await res.json()) as {
      last_reading?: string | null; last_reading_age_h?: number | null;
      last_push?: string | null; readings_7d?: number; readings_total?: number;
    } : null))
    .then((st) => {
      line.remove();
      if (st === null) {
        para(pf, "Not available on this console.");
        return;
      }
      if (st.last_reading === null || st.last_reading === undefined) {
        para(pf, "Nothing received yet. When the plant's job starts posting its " +
          "hourly export, the last reading and its age appear here.");
        return;
      }
      const age = st.last_reading_age_h ?? 0;
      row(pf, "Last reading", st.last_reading.replace("T", " ").slice(0, 16),
        `${age.toFixed(1)} h ago · ${st.readings_7d ?? 0} readings in 7 d`);
      row(pf, "Last push", (st.last_push ?? "—").replace("T", " ").slice(0, 16) + " UTC",
        `${(st.readings_total ?? 0).toLocaleString()} readings held`);
    })
    .catch(() => {
      line.textContent = "Could not reach the feed status.";
    });

  const m = section(into, "Model");
  row(m, "Kind", p.model?.kind ?? "—");
  row(m, "Trained", `${p.model?.trained_days ?? "—"} days`,
    `${p.model?.trained_from ?? "?"} → ${p.model?.trained_to ?? "?"}`);
  row(m, "Form", p.model?.form ?? "—");

  const w = section(into, "What breaks if one goes");
  para(w, "If buoy 46215 drops, the model refits on Port San Luis and 46218 and " +
    "keeps forecasting at reduced accuracy. If the sea-surface field is missing, " +
    "the map says so rather than drawing calm water. If this panel stops being " +
    "rebuilt, the date above stops advancing — the failure that looks most like " +
    "success, and the reason it is the first row.");
}

// --- Feedback ----------------------------------------------------------------
function feedback(into: HTMLElement): void {
  const b = section(into, "Tell us what is wrong or missing");
  para(b, "Anything on this console that reads oddly, or a number you cannot " +
    "reconcile with your own instrument, is worth sending. Wrong numbers are " +
    "more useful to us than silence.");
  const a = el("a", "btn");
  a.textContent = "Open feedback";
  a.href = "/feedback";
  b.appendChild(a);
}
